import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Shield, RefreshCw, Loader2, AlertCircle, CheckCircle2, X, Download, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall } from '@/lib/api';

interface Row {
  id: string;
  organization_id: string | null;
  member_id: string | null;
  email: string;
  type: 'export' | 'delete';
  status: 'pending' | 'approved' | 'completed' | 'rejected' | 'cancelled';
  reason: string | null;
  resolved_by_user_id: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export default function DataRequests() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Row[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const query: Record<string, string | number> = { page, per_page: 50 };
      if (filter === 'pending') query.status = 'pending';
      const r = await apiCall<{ items: Row[] } & PageMeta>('/v1/data-requests', { bearerToken: token, query });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, filter, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  async function resolve(id: string, status: 'completed' | 'rejected') {
    const verb = status === 'completed' ? 'complete' : 'reject';
    const notes = prompt(`Add notes for this ${verb}d request (optional):`) ?? '';
    if (!confirm(`${verb.toUpperCase()} this request?${status === 'completed' ? '\n\nFor delete requests, this WILL permanently remove the member record.' : ''}`)) return;
    setBusy(id);
    try {
      const token = await getToken();
      await apiCall(`/v1/data-requests/${id}`, {
        method: 'PATCH', bearerToken: token,
        body: { status, resolution_notes: notes.trim() || null },
      });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Shield className="h-6 w-6" /> Data requests</h1>
          <p className="text-sm text-muted-foreground">Member-initiated data export + delete requests. Review and action within 7 days.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="border-b flex items-center gap-1">
        {(['pending', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={'px-3 py-2 text-sm border-b-2 -mb-px capitalize ' + (filter === f ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {f}
          </button>
        ))}
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          {filter === 'pending' ? 'No pending requests.' : 'No requests yet.'}
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {items.map((r) => (
          <Card key={r.id}>
            <CardContent className="py-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {r.type === 'delete'
                  ? <Trash2 className="h-4 w-4 text-destructive shrink-0" />
                  : <Download className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.type === 'delete' ? 'requested deletion' : 'requested export'} · {new Date(r.created_at.replace(' ', 'T') + 'Z').toLocaleString()}
                  </div>
                  {r.reason && <div className="text-xs text-muted-foreground mt-1 italic">"{r.reason}"</div>}
                </div>
              </div>
              <Badge variant={r.status === 'pending' ? 'outline' : r.status === 'completed' ? 'default' : r.status === 'rejected' ? 'destructive' : 'outline'} className="capitalize">{r.status}</Badge>
              {r.status === 'pending' && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7" onClick={() => void resolve(r.id, 'rejected')} disabled={busy === r.id}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button size="sm" className="h-7" onClick={() => void resolve(r.id, 'completed')} disabled={busy === r.id}>
                    {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                    Complete
                  </Button>
                </div>
              )}
              {r.resolution_notes && (
                <details className="text-xs text-muted-foreground basis-full">
                  <summary className="cursor-pointer">resolution notes</summary>
                  <p className="mt-1">{r.resolution_notes}</p>
                </details>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="requests" />
    </div>
  );
}
